import React, { useMemo } from 'react';

import i18n from '@client/utils/i18n';
import { useTheme } from '@mui/material/styles';
import { bisector, max } from 'd3-array';
import { scaleLinear } from 'd3-scale';
import { area, curveLinear } from 'd3-shape';

interface CircadianTooltipData {
    time: string;
    spectrumMixName: string;
    lightRecipeName: string;
}

interface CircadianDataPoint {
    time: number;
    value: number;
    tooltip?: CircadianTooltipData;
    color?: { uv: number; blue: number; green: number; red: number; fr: number; ir: number };
}

// Representative colors for each spectral band, derived from wavelengthToColor at the
// band's center wavelength. Alpha values < 1 replicate the fading used in WavelengthChart
// for out-of-visible-range bands (UV as faint violet, FR/IR as faint red).
const SPECTRAL_BANDS: { key: keyof NonNullable<CircadianDataPoint['color']>; r: number; g: number; b: number; alpha: number }[] = [
    // 370nm → faint violet
    { key: 'uv', r: 1, g: 0, b: 1, alpha: 0.27 },
    // 450nm → blue
    { key: 'blue', r: 0, g: 0.2, b: 1, alpha: 1.0 },
    // 550nm → yellow-green
    { key: 'green', r: 0.571, g: 1, b: 0, alpha: 1.0 },
    // 660nm → red
    { key: 'red', r: 1, g: 0, b: 0, alpha: 1.0 },
    // 730nm → dim red
    { key: 'fr', r: 1, g: 0, b: 0, alpha: 0.58 },
    // 800nm → faint red
    { key: 'ir', r: 1, g: 0, b: 0, alpha: 0.26 }
];

interface CircadianChartProps {
    data: CircadianDataPoint[];
    referencePercent?: number;
    unit?: 'pfd' | 'irradiance';
    mini?: boolean;
}

const BASE_WIDTH = 600;
const ASPECT = 2.5;
const MARGIN = { top: 10, right: 15, bottom: 50, left: 65 };
const TICK_SIZE = 5;

let gradientIdCounter = 0;

const tickFormatter = (v: number) => parseFloat(v.toPrecision(2)).toString();

const dataBisector = bisector<CircadianDataPoint, number>((d) => d.time).left;

function computeStopColor(d: CircadianDataPoint, yDomainMax: number): string {
    if (!d.color) {
        return 'rgba(119,119,119,0.75)';
    }
    let accR = 0,
        accG = 0,
        accB = 0,
        totalWeight = 0;
    for (const band of SPECTRAL_BANDS) {
        const bandValue = d.color[band.key];
        const weight = bandValue * band.alpha;
        accR += weight * band.r;
        accG += weight * band.g;
        accB += weight * band.b;
        totalWeight += weight;
    }
    if (totalWeight === 0 || yDomainMax === 0) {
        return 'rgba(0,0,0,0.75)';
    }
    const brightness = d.value / yDomainMax;
    const rn = (accR / totalWeight) * brightness;
    const gn = (accG / totalWeight) * brightness;
    const bn = (accB / totalWeight) * brightness;
    return `rgba(${Math.round(rn * 255)},${Math.round(gn * 255)},${Math.round(bn * 255)},0.75)`;
}

const CircadianChart = ({ data, referencePercent, unit = 'pfd', mini = false }: CircadianChartProps) => {
    const theme = useTheme();
    if (!data.some((e) => e.time === 0)) {
        data.unshift({ time: 0, value: 0 });
    }
    if (!data.some((e) => e.time === 23 + 55 / 60)) {
        data.push({ time: 23 + 55 / 60, value: 0 });
    }

    let reference: number | null = null;
    if (referencePercent) {
        reference = (24 * referencePercent) / 100;
        if (reference === 0) {
            reference = 0.001;
        }
    }

    const gradientId = React.useRef(`cg-${++gradientIdCounter}`).current;
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = React.useState(BASE_WIDTH);
    const [tooltip, setTooltip] = React.useState<{ x: number; y: number; data: CircadianTooltipData } | null>(null);
    const [colorMode] = React.useState(false);

    React.useEffect(() => {
        const el = containerRef.current;
        if (!el) {
            return;
        }
        const observer = new ResizeObserver(([entry]) => {
            setContainerWidth(entry.contentRect.width);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const scale = BASE_WIDTH / Math.max(containerWidth, 1);
    const fontSize = Math.round(14 * scale);
    const strokeWidth = scale;
    const dotRadius = 2 * scale;
    const marginBottom = mini ? 20 : MARGIN.bottom;
    const marginTop = mini ? MARGIN.top + 5 : MARGIN.top;
    const totalHeight = Math.round(BASE_WIDTH / ASPECT);
    const innerWidth = BASE_WIDTH - MARGIN.left - MARGIN.right;
    const innerHeight = totalHeight - marginTop - marginBottom;

    const xScale = useMemo(() => scaleLinear().domain([0, 24]).range([0, innerWidth]), [innerWidth]);

    const yDomainMax = useMemo(() => {
        const dataMax = max(data, (d) => d.value) ?? 0;
        return dataMax > 0 ? dataMax : 1;
    }, [data]);

    const yScale = useMemo(() => scaleLinear().domain([0, yDomainMax]).nice().range([innerHeight, 0]), [yDomainMax, innerHeight]);

    const areaPath = useMemo(() => {
        const gen = area<CircadianDataPoint>()
            .x((d) => xScale(d.time))
            .y0(innerHeight)
            .y1((d) => yScale(d.value))
            .curve(curveLinear);
        return gen(data) ?? '';
    }, [data, xScale, yScale, innerHeight]);

    const gradientStops = useMemo(() => {
        if (!colorMode) {
            return [];
        }
        return data.map((d) => ({
            offset: xScale(d.time) / innerWidth,
            stopColor: computeStopColor(d, yDomainMax)
        }));
    }, [colorMode, data, xScale, innerWidth, yDomainMax]);

    const xTicks = useMemo(() => [...Array(24).keys()], []);
    const yTicks = useMemo(() => {
        if (mini) {
            const [, domainMax] = yScale.domain();
            return [0, domainMax / 2, domainMax];
        }
        return yScale.ticks(5);
    }, [yScale, mini]);

    function handleMouseMove(e: React.MouseEvent<SVGSVGElement>) {
        if (!containerRef.current) {
            return;
        }
        const rect = containerRef.current.getBoundingClientRect();
        const svgX = (e.clientX - rect.left) * (BASE_WIDTH / rect.width);
        const dataX = xScale.invert(svgX - MARGIN.left);
        const idx = dataBisector(data, dataX);
        const candidates = [data[idx - 1], data[idx]].filter(Boolean);
        if (!candidates.length) {
            setTooltip(null);
            return;
        }
        const closest = candidates.reduce((a, b) => (Math.abs(a.time - dataX) <= Math.abs(b.time - dataX) ? a : b));
        if (closest.tooltip) {
            setTooltip({ x: e.clientX - rect.left, y: e.clientY - rect.top, data: closest.tooltip });
        } else {
            setTooltip(null);
        }
    }

    const fillColor = colorMode ? `url(#${gradientId})` : theme.palette.text.disabled;
    const fillOpacity = colorMode ? 1 : 0.1;
    const strokeColor = colorMode ? `url(#${gradientId})` : theme.palette.text.disabled;

    return (
        <div ref={containerRef} style={{ position: 'relative', width: '100%', backgroundColor: theme.palette.background.paper }}>
            <svg
                viewBox={`0 0 ${BASE_WIDTH} ${totalHeight}`}
                width="100%"
                style={{ display: 'block', userSelect: 'none' }}
                onMouseMove={handleMouseMove}
                onMouseLeave={() => setTooltip(null)}
            >
                {colorMode && gradientStops.length > 0 && (
                    <defs>
                        <linearGradient id={gradientId} gradientUnits="userSpaceOnUse" x1={0} y1="0" x2={innerWidth} y2="0">
                            {gradientStops.map((s, i) => (
                                <stop key={i} offset={s.offset} stopColor={s.stopColor} />
                            ))}
                        </linearGradient>
                    </defs>
                )}
                <g transform={`translate(${MARGIN.left},${marginTop})`}>
                    {yTicks.map((tick) => (
                        <line
                            key={`gy-${tick}`}
                            x1={0}
                            x2={innerWidth}
                            y1={yScale(tick)}
                            y2={yScale(tick)}
                            stroke={theme.palette.divider}
                            strokeWidth={strokeWidth}
                            strokeDasharray={`${3 * scale} ${3 * scale}`}
                        />
                    ))}
                    {xTicks.map((tick) => (
                        <line
                            key={`gx-${tick}`}
                            x1={xScale(tick)}
                            x2={xScale(tick)}
                            y1={0}
                            y2={innerHeight}
                            stroke={theme.palette.divider}
                            strokeWidth={strokeWidth}
                            strokeDasharray={`${3 * scale} ${3 * scale}`}
                        />
                    ))}

                    <path d={areaPath} fill={fillColor} fillOpacity={fillOpacity} stroke={strokeColor} strokeWidth={strokeWidth} />

                    {data.map((d, i) => {
                        const dotColor = colorMode && d.color ? computeStopColor(d, yDomainMax).replace(/,[\d.]+\)$/, ',1)') : theme.palette.text.disabled;
                        return <circle key={i} cx={xScale(d.time)} cy={yScale(d.value)} r={dotRadius} stroke={dotColor} fill={dotColor} />;
                    })}

                    {reference !== null && (
                        <line x1={xScale(reference)} x2={xScale(reference)} y1={0} y2={innerHeight} stroke="red" strokeWidth={strokeWidth} />
                    )}

                    <g transform={`translate(0,${innerHeight})`}>
                        <line x1={0} x2={innerWidth} stroke={theme.palette.text.disabled} strokeWidth={strokeWidth} />
                        {xTicks.map((tick) => (
                            <g key={tick} transform={`translate(${xScale(tick)},0)`}>
                                <line y2={TICK_SIZE * scale} stroke={theme.palette.text.disabled} strokeWidth={strokeWidth} />
                                {!mini && (
                                    <text
                                        y={TICK_SIZE * scale + 4 * scale}
                                        dy="0.71em"
                                        textAnchor="middle"
                                        fontSize={fontSize}
                                        fill={theme.palette.text.secondary}
                                    >
                                        {tick}
                                    </text>
                                )}
                            </g>
                        ))}
                    </g>

                    <g>
                        <line y1={0} y2={innerHeight} stroke={theme.palette.text.disabled} strokeWidth={strokeWidth} />
                        {yTicks.map((tick) => (
                            <g key={tick} transform={`translate(0,${yScale(tick)})`}>
                                <line x2={-TICK_SIZE * scale} stroke={theme.palette.text.disabled} strokeWidth={strokeWidth} />
                                <text x={(-TICK_SIZE - 3) * scale} dy="0.32em" textAnchor="end" fontSize={fontSize} fill={theme.palette.text.secondary}>
                                    {tickFormatter(tick)}
                                </text>
                            </g>
                        ))}
                        <text
                            transform={`translate(${-(MARGIN.left - 22) * scale},${innerHeight / 2}) rotate(-90)`}
                            textAnchor="middle"
                            fontSize={fontSize}
                            fill={theme.palette.text.secondary}
                        >
                            {`${i18n(unit) as string} (${i18n(unit + '_unit') as string})`}
                        </text>
                    </g>
                    {!mini && (
                        <g transform={`translate(0,${innerHeight + 35})`}>
                            <rect
                                x={0}
                                y={-6 * scale}
                                width={12 * scale}
                                height={12 * scale}
                                fill={theme.palette.text.disabled}
                                fillOpacity={0.1}
                                stroke={theme.palette.text.disabled}
                                strokeWidth={strokeWidth}
                            />
                            <text x={16 * scale} dy="0.32em" fontSize={fontSize} fill={theme.palette.text.secondary}>
                                {i18n('hours') as string}
                            </text>
                        </g>
                    )}
                </g>
            </svg>

            {tooltip && (
                <div
                    style={{
                        position: 'absolute',
                        left: tooltip.x + 10,
                        top: tooltip.y - 10,
                        background: theme.palette.background.paper,
                        padding: '5px 8px',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '4px',
                        fontSize: '11px',
                        pointerEvents: 'none',
                        zIndex: 10
                    }}
                >
                    <div>{tooltip.data.time}</div>
                    <div>{tooltip.data.spectrumMixName}</div>
                    <div>{tooltip.data.lightRecipeName}</div>
                </div>
            )}
        </div>
    );
};

export default React.memo(CircadianChart);
